import { InformationFilled, Renew } from '@carbon/icons-react';
import * as React from 'react';
import {
  Input,
  OutlinedIconButton,
  StaticBadge,
} from 'vet-bones/design-system/components';

import * as copy from 'src/templates/copy';

import styles from 'src/components/resourceDialog/styles/resourcePreviewHeader.module.scss';

export type ResourcePreviewHeaderProps = {
  /** The current resource name */
  resourceName: string;
  /** Callback when the resource name changes */
  onResourceNameChange: (name: string) => void;
  /** Placeholder text for the name input */
  namePlaceholder: string;
  /** ID for the name input element */
  nameInputId: string;
  /** Test ID for the name input element */
  nameInputTestId?: string;
  /** Last modified date string to display */
  lastModified?: string | null;
  /** Whether to show the partial data warning badge */
  showPartialDataWarning?: boolean;
  /** Optional callback for refresh button (only shown if provided) */
  onRefresh?: () => void;
};

/**
 * A shared header component for resource previews.
 *
 * This component consolidates the common header pattern used by:
 * - CsvPreviewContent (file upload previews)
 * - VirtualGraphPreview (virtual graph previews)
 *
 * Features:
 * - Resource name input
 * - Last modified timestamp display
 * - Partial data warning badge
 * - Optional refresh button (for Virtual Graphs)
 *
 * @example
 * ```tsx
 * <ResourcePreviewHeader
 *   resourceName={fileName}
 *   onResourceNameChange={setFileName}
 *   namePlaceholder="Enter file name..."
 *   nameInputId="file-name-input"
 *   lastModified={lastModified}
 *   showPartialDataWarning={hasRowLimitWarning}
 * />
 * ```
 */
export const ResourcePreviewHeader: React.VFC<ResourcePreviewHeaderProps> = ({
  resourceName,
  onResourceNameChange,
  namePlaceholder,
  nameInputId,
  nameInputTestId,
  lastModified,
  showPartialDataWarning,
  onRefresh,
}) => {
  return (
    <div className={styles.fileInfo}>
      <div className={styles.headerRow}>
        <Input
          className={styles.fileName}
          clearable={false}
          id={nameInputId}
          label=""
          minimal
          onChange={(e) => {
            onResourceNameChange(e.target.value);
          }}
          placeholder={namePlaceholder}
          size="lg-inside"
          testId={nameInputTestId}
          value={resourceName}
        />
        <div className={styles.headerRight}>
          {lastModified ? (
            <div className={styles.lastUpdated}>
              {copy.components.projectResourceDialog.fileUploadPanelV2.lastUpdated(
                lastModified
              )}
            </div>
          ) : null}
          {onRefresh ? (
            <OutlinedIconButton leftIcon={Renew} onClick={onRefresh} />
          ) : null}
          {showPartialDataWarning ? (
            <>
              {lastModified || onRefresh ? (
                <span className={styles.headerDivider}>/</span>
              ) : null}
              <StaticBadge
                leftIcon={InformationFilled}
                size="md"
                type="info"
                variant="muted"
              >
                {
                  copy.components.projectResourceDialog.fileUploadPanelV2
                    .mayNotReflectFullData
                }
              </StaticBadge>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};
